import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Ensure a pending_payment order exists for a finished auction won by the
 * calling user. Safe to call repeatedly — the DB enforces one order per
 * auction (unique index on orders.auction_id).
 *
 * Never marks the order paid. Never touches payout status. If close_expired_auctions
 * hasn't run yet, we invoke it first so the order is created with the correct
 * commission split from platform_settings.
 */
export const ensurePendingOrderForAuction = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ auctionId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    // Best-effort: close the auction (idempotent). This creates the order
    // server-side with amount/commission derived from the DB, never the client.
    try {
      await supabase.rpc("close_expired_auctions" as never);
    } catch {
      /* ignore — we'll re-check below */
    }

    const { data: auction, error: aErr } = await supabase
      .from("auctions")
      .select("id,seller_id,winner_id,high_bidder_id,status,end_time")
      .eq("id", data.auctionId)
      .maybeSingle();
    if (aErr) throw new Error(aErr.message);
    if (!auction) throw new Error("Auction not found");

    const ended =
      auction.status !== "active" || new Date(auction.end_time) <= new Date();
    if (!ended) throw new Error("Auction has not ended yet");

    const winnerId = auction.winner_id ?? auction.high_bidder_id;
    if (!winnerId) throw new Error("This auction has no winner");
    if (winnerId !== userId) throw new Error("Only the winning bidder can pay for this order");

    const { data: order, error: oErr } = await supabase
      .from("orders")
      .select("id,status,buyer_id,has_active_dispute")
      .eq("auction_id", data.auctionId)
      .maybeSingle();
    if (oErr) throw new Error(oErr.message);

    if (!order) {
      // close_expired_auctions should have created it. If it didn't, the
      // auction likely ended without a winning bid — surface a clear error
      // rather than trying to synthesise an order client-side.
      throw new Error("Order is not yet available — please refresh in a moment");
    }
    if (order.buyer_id !== userId) throw new Error("Not your order");

    return {
      orderId: order.id,
      status: order.status,
      hasActiveDispute: order.has_active_dispute,
    };
  });
